<template>
  <SvgSprite />
  <div class="as-content-page as-match-detail" ref="asMatchDetail">
    <TabsIcon
      :tabs="tabs"
      :activeTab="activeTab"
      :isMin="isChatTyping"
      @change="changeTab"
    />

    <div
      id="as-match-detail-content"
      class="as-match-detail-content"
      :class="{
        'as-hide-content': isHideContent,
      }"
      :style="{
        height: `${heightContent}px`,
      }"
    >
      <div v-show="activeTab === 'chat'">
        <IframeChat />
      </div>

      <div v-if="activeTab === 'statistics'">
        <Transition name="as-fade" mode="out-in">
          <StatisticsList
            v-if="tabLoading.statistics"
            :match="match"
            :statistics="statistics"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'overview'">
        <Transition name="as-fade" mode="out-in">
          <IncidentsList
            v-if="tabLoading.overview"
            :match="match"
            :detailShooting="detailShooting"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'lineups'">
        <Transition name="as-fade" mode="out-in">
          <LineupTab
            v-if="tabLoading.lineups"
            :match="match"
            :lineups="lineups"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div
        v-else-if="activeTab === 'standing'"
        class="as-h-100 as-position-relative"
      >
        <Transition name="as-fade" mode="out-in">
          <StandingListTab
            v-if="tabLoading.standing"
            :match="match"
            :standing="standing"
          />
          <StandingListTabSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'shooting'">
        <Transition name="as-fade" mode="out-in">
          <ShootingListTab
            v-if="tabLoading.shooting"
            :match="match"
            :shooting="shootingPointData"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'h2h'">
        <Transition name="as-fade" mode="out-in">
          <H2hList
            v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
            :match="match"
            :h2h="h2h"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div
        v-else-if="activeTab === 'next_match'"
        :class="`as-parent-h2h as-parent-h2h__item-h-auto as-parent-h2h-${siteName}`"
      >
        <Transition name="as-fade" mode="out-in">
          <NextMatchList
            v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
            :match="match"
            :h2h="h2h"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>

      <div
        v-else-if="activeTab === 'match_other'"
        :class="`as-parent-h2h as-parent-h2h__item-h-auto as-parent-h2h-${siteName}`"
      >
        <Transition name="as-fade" mode="out-in">
          <MatchOther
            v-if="tabLoading.match_other"
            :matchLiveData="matchOtherData"
            :matchId="matchId"
            mode="match-other"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'odd'">
        <OddTab
          :odd="odd"
          :oddOld="oddOld"
          :tabKey="tabKey.odd"
        />
      </div>
      <div v-else-if="activeTab === 'information'">
        <Information :match="match" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import H2hListSkeleton from "~src/components/skeleton/result-popup/H2hListSkeleton.vue";
import StandingListTabSkeleton from "~src/components/skeleton/standing/StandingListTabSkeleton.vue";
import {
  matchStatistics,
  matchH2h,
  matchStanding,
  matchLineups,
  matchDetail,
  matchDetailLive,
  shootingPointsMatch,
  getRealtimeDetail,
} from "~src/services/SpapiService";
import { getOddsMatch } from "~src/services/OddService";
import { getMatchLive } from "~src/services/RemoteLiveService";
import {
  addMatchDetailLive,
  initializeData,
  convertShootingPointsData,
  groupByPlayer,
} from "~src/helpers/basketball";
import { convertLineupsData } from "~src/helpers/basketball/LineupHelper";
import type {
  IMatch,
  IMatchLiveData,
  IOddMatch,
  IItemString,
  IStatsJsonData,
} from "~src/interfaces/basketball";
import IncidentsList from "~src/components/incidents/basketball/IncidentsList.vue";
import StatisticsList from "~src/components/statistics/basketball/StatisticsList.vue";
import H2hList from "~src/components/h2h/basketball/H2hList.vue";
import StandingListTab from "~src/components/standings/basketball/StandingListTab.vue";
import ShootingListTab from "~src/components/shooting/basketball/ShootingListTab.vue";
import LineupTab from "~src/components/lineups/basketball/LineupTab.vue";
import Information from "~src/components/matches/Information.vue";
import MatchOther from "~src/components/matches/basketball/MatchOther.vue";
import NextMatchList from "~src/components/matches/basketball/NextMatchList.vue";
import OddTab from "~src/components/odds/OddTab.vue";
import IframeChat from "~src/components/IframeChat.vue";
import IconStats from "~src/components/icons/menus/IconStats.vue";
import IconSummary from "~src/components/icons/menus/basketball/IconSummary.vue";
import IconLineUp from "~src/components/icons/menus/IconLineUp.vue";
import IconShooting from "~src/components/icons/menus/IconShooting.vue";
import IconStanding from "~src/components/icons/menus/IconStanding.vue";
import IconChat from "~src/components/icons/menus/IconChat.vue";
import IconH2h from "~src/components/icons/menus/IconH2h.vue";
import IconMatchOther from "~src/components/icons/menus/IconMatchOther.vue";
import IconOdd from "~src/components/icons/menus/IconOdd.vue";
import IconInfo from "~src/components/icons/menus/IconInfo.vue";
import IconLive from "~src/components/icons/menus/IconLive.vue";
// import IconData from "~src/components/icons/menus/IconData.vue";
import SvgSprite from "~src/components/matches/SvgSprite.vue";
import MatchEnum from "~src/enums/basketball/MatchEnum";
import TabsIcon from "~src/components/TabsIcon.vue";
import { useMatchDetailStore } from "~src/stores/MatchDetailStore";
import { isObjectNotEmpty } from "~src/helpers";
import { EVENT_ODDS_LIVE_DATA_UPDATED } from "~src/utils/constant";

export default defineComponent({
  name: "matchDetail",
  components: {
    H2hListSkeleton,
    StandingListTabSkeleton,
    IncidentsList,
    StatisticsList,
    H2hList,
    StandingListTab,
    ShootingListTab,
    LineupTab,
    MatchOther,
    NextMatchList,
    OddTab,
    Information,
    IframeChat,
    SvgSprite,
    TabsIcon,
  },
  props: {
    matchId: {
      type: String,
    },
    apiLiveUrl: {
      type: String,
    }
  },
  data() {
    const matchDetailStore = useMatchDetailStore();

    return {
      matchDetailStore,
      match: {} as IMatch,
      incidents: [],
      statistics: [] as IStatsJsonData[] | any,
      h2h: {},
      standing: {},
      lineups: {},
      shootingPointData: null as any,
      detailShooting: null as any,
      data: null,
      odd: [] as IOddMatch[],
      oddOld: [] as IOddMatch[],
      matchOtherData: {} as IMatchLiveData,
      matchOthers: [] as Array<IMatch>,
      isLoading: false,
      isLoadingInterval: false,
      isShowChat: true,
      tabs: [
        {
          key: "statistics",
          title: "Thông số",
          icon: IconStats,
        },
        {
          key: "overview",
          title: "Chi tiết",
          icon: IconSummary,
        },
        {
          key: "shooting",
          title: "Hộp điểm",
          icon: IconShooting,
        },
        {
          key: "lineups",
          title: "Cầu thủ chính",
          icon: IconLineUp,
        },
        {
          key: "standing",
          title: "BXH",
          icon: IconStanding,
        },
        {
          key: "chat",
          title: "Chat",
          icon: IconChat,
        },
        {
          key: "h2h",
          title: "Đối đầu",
          icon: IconH2h,
        },
        {
          key: "match_other",
          title: "Trận khác",
          icon: IconLive,
        },
        {
          key: "next_match",
          title: "Trận sắp tới",
          icon: IconMatchOther,
        },
        {
          key: "odd",
          title: "Tỷ lệ kèo",
          icon: IconOdd,
        },
        {
          key: "information",
          title: "TT trận",
          icon: IconInfo,
        },
        // {
        //   key: "data",
        //   title: "Dữ liệu",
        //   icon: IconData,
        // },
      ],
      tabLoading: {} as IItemString,
      tabKey: {} as IItemString,
      timers: [] as NodeJS.Timeout[]
    };
  },
  computed: {
    activeTab(): string {
      return this.matchDetailStore.getActiveTab;
    },
    isChatTyping(): boolean {
      return this.matchDetailStore.getIsChatTyping;
    },
    isHideContent(): boolean {
      return this.matchDetailStore.getIsHideContent;
    },
    heightContent(): number {
      return this.matchDetailStore.getHeightContent;
    },
    sportType() {
      // @ts-ignore
      return this.gSportType;
    },
    siteName() {
      // @ts-ignore
      return this.gSiteName;
    },
    isLiveSite(): boolean {
      // @ts-ignore
      return this.gIsLiveSite;
    },
  },
  async created() {
    try {
      const rsMatch = await matchDetail(this.matchId, this.sportType);
        this.match =
          rsMatch.data.data && rsMatch.data.data.data
          ? rsMatch.data.data.data
          : rsMatch.data.data;

      await this.changeTab("chat");
    } catch (e) {
      console.log("Load match detail error", e);
    }
  },
  async mounted() {
    const element = this.$refs.asMatchDetail as HTMLElement;
    if (element) {
      // @ts-ignore
      this.matchDetailStore.setScreenWidth(
        element?.getBoundingClientRect().width
      );
    }

    const interval1 = setInterval(async () => {
      if (
        this.match.status_id !== MatchEnum.STATUS_END &&
        !this.isLoadingInterval &&
        this.activeTab !== "odd"
      ) {
        this.isLoadingInterval = true;
        await this.changeTab(this.activeTab, true);
        this.isLoadingInterval = false;
      }
    }, 21000);
    this.timers.push(interval1);

    if (this.isLiveSite) {
      window.addEventListener(EVENT_ODDS_LIVE_DATA_UPDATED, this.handleOddsLiveDataUpdated);
    } else {
      const interval2 = setInterval(async () => {
        if (
          this.match.status_id !== MatchEnum.STATUS_END &&
          !this.isLoadingInterval &&
          this.activeTab === "odd"
        ) {
          this.isLoadingInterval = true;
          await this.changeTab(this.activeTab, true);
          this.isLoadingInterval = false;
        }
      }, 5000);
      this.timers.push(interval2);
    }
  },
  async beforeUnmount() {
    this.timers.forEach(clearInterval);

    if (this.isLiveSite) {
      window.removeEventListener(EVENT_ODDS_LIVE_DATA_UPDATED, this.handleOddsLiveDataUpdated);
    }
  },
  methods: {
    async changeTab(tabName: string, isInterval = false) {
      this.matchDetailStore.setActiveTab(tabName);
      const matchId = this.matchId;
      const seasonId = this.match?.season_id ?? "";
      this.isLoading = true;

      if (tabName === "statistics") {
        try {
          const {data: { data: statistics = [] }} = await matchStatistics(matchId, this.sportType);
          if (statistics) {
            this.statistics = statistics;
          } else {
            this.statistics = [initializeData()];
          }
        } catch (e) {
          console.log("Load statistics error", e);
        }
      } else if (tabName === "overview") {
        try {
          const rs = await getRealtimeDetail(matchId, this.sportType);
          const { data } = rs.data;
          const rsLineups = await matchLineups(matchId, this.sportType);
          this.lineups = rsLineups.data.data
            ? convertLineupsData(rsLineups.data.data)
            : {};
          this.detailShooting = {
            match: data.match,
            shootingPoints: data.data.reverse(),
            lineups: this.lineups,
          };
        } catch (e) {
          this.detailShooting = {};
        }
      } else if (tabName === "h2h" || tabName === "next_match") {
        try {
          const {data: { data: h2h = {} }} = await matchH2h(matchId, this.sportType);
          this.h2h = h2h;
        } catch (e) {
          console.log("Load h2h error", e);
        }
      } else if (tabName === "match_other") {
        try {
          if (isInterval) {
            const rsMatch = await getMatchLive(this.apiLiveUrl, this.sportType, this.siteName);
            const rsLive = await matchDetailLive(this.sportType);
            const matches = await addMatchDetailLive(
              this.matchOtherData.matches,
              rsLive.data.results,
              rsMatch.data.competitions,
            );
            this.matchOtherData.matches = matches;
          } else {
            const [rsMatch, rsLive] = await Promise.all([
              getMatchLive(this.apiLiveUrl, this.sportType, this.siteName),
              matchDetailLive(this.sportType),
            ]);
            const matches = await addMatchDetailLive(
              rsMatch.data.matches,
              rsLive.data.results,
              rsMatch.data.competitions,
            );
            this.matchOtherData = {
              competitions: rsMatch.data.competitions,
              teams: rsMatch.data.teams,
              commentators: rsMatch.data.commentators,
              matches,
            } as IMatchLiveData;
          }
        } catch (e) {
          console.log("Load match other error", e);
        }
      } else if (tabName === "standing") {
        if (seasonId) {
          try {
            const { data: { data: standing = {} } } = await matchStanding(seasonId, this.sportType);
            this.standing = {
              match: this.match,
              ...standing,
            };
          } catch (e) {
            console.log("Load standing error", e);
          }
        }
      } else if (tabName === "lineups") {
        try {
          const { data: { data: lineups = {} } } = await matchLineups(matchId, this.sportType);
          this.lineups = lineups
            ? convertLineupsData(lineups)
            : {};
        } catch (e) {
          console.log("Load lineups error", e);
        }
      } else if (tabName === "odd") {
        try {
          const { data: { data: odd = [] }} = await getOddsMatch(this.sportType, this.matchId);
          this.oddOld = JSON.parse(JSON.stringify(this.odd));
          this.odd = odd && odd.length ? odd : [];
        } catch (e) {
          console.log("Load odd error", e);
        }
      } else if (tabName === "shooting") {
        try {
          const {data: { data: shootingPoints = {} }} = await shootingPointsMatch(matchId, this.sportType);
          const lineupsRealtimeData = await matchLineups(
            matchId,
            this.sportType
          );
          if (!shootingPoints && !lineupsRealtimeData?.data?.data) {
            this.shootingPointData = {};
          }
          const shootingPointsConverted = convertShootingPointsData(shootingPoints);
          const groupByPlayerData = groupByPlayer(shootingPoints?.shootingPoints);
          this.shootingPointData = {
            shootingPointsConverted,
            match: this.match,
            listPlayer: shootingPoints?.listPlayer,
            shootingPoints: shootingPoints?.shootingPoints,
            groupByPlayerData,
            lineup: lineupsRealtimeData?.data?.data,
          };
        } catch (e) {
          console.log("Load shooting error", e);
        }
      }
      this.isLoading = false;
      this.tabKey[tabName] = Date.now();
      this.tabLoading[tabName] = true;
    },
    isObjectNotEmpty,
    handleOddsLiveDataUpdated(e: Event) {
      const oddsData = (e as CustomEvent<IOddMatch[]>).detail;
      if (oddsData && this.activeTab === "odd") {
        this.oddOld = JSON.parse(JSON.stringify(this.odd));
        this.odd = oddsData;
      }
    },
  },
});
</script>

<style scoped></style>
